// /home/coder/project/frontend/src/pages/ToCompDetail.tsx

import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query'; // Keep for company query
import { useGetCountries } from '../../../hooks/genericHooks'; // Import hook for countries
import { Card, Space, message, Typography, Row, Col, Descriptions, Spin, Alert, Tag } from 'antd';
import { RollbackOutlined, EditOutlined, BankOutlined as CompanyIcon } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchSqlData } from '../../../services/api';
import type { Company, CorCountries } from '../../../types'; // Import CorCountries
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';
import Button from '../../../components/common/Button';

const { Title } = Typography;

// --- Relation Mapping and Helper ---
const relationOptions = [
  { value: 1, label: 'Root' }, { value: 2, label: 'Partner' }, { value: 3, label: 'Client' },
  { value: 11, label: 'Root Subcon' }, { value: 12, label: 'Root Vendor' },
  { value: 21, label: 'Partner Subcon' }, { value: 22, label: 'Partner Vendor' },
  { value: 33, label: 'Client Subcon' }, { value: 34, label: 'Client Vendor' },
  { value: 35, label: 'Other' },
];

const getRelationLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  const option = relationOptions.find(opt => opt.value === value);
  return option ? option.label : String(value);
};
// -----------------------------------

// Helper function to format enum strings
const formatEnumString = (value: string | undefined | null): string => {
    if (!value) return '-';
    const strValue = String(value);
    return strValue.charAt(0).toUpperCase() + strValue.slice(1);
};
// Helper to get color for status tag
const getStatusColor = (status: string | undefined | null): string => {
    const strStatus = String(status).toLowerCase();
    switch (strStatus) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
};

const ToCompDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const companyId = id || '';

  // --- Data Fetching ---
  // Fetch specific company
  const { data: companyDataArray, isLoading: isLoadingCompany, isError: isErrorCompany, error: errorCompany } = useQuery<Company[], Error>({
    queryKey: ['companies', companyId],
    queryFn: () => fetchSqlData('to_companies', 'id', companyId),
    enabled: !!companyId,
  });

  // Fetch countries
  const { data: countriesData, isLoading: isLoadingCountries, isError: isErrorCountries, error: errorCountries } = useGetCountries();
  const countries = countriesData?.data || [];

  // Combine loading/error states
  const isLoading = isLoadingCompany || isLoadingCountries;
  const isError = isErrorCompany || isErrorCountries;
  const error = errorCompany || errorCountries;

  // --- Data Processing ---
  const companyData = useMemo(() => {
    return companyDataArray && companyDataArray.length > 0 ? companyDataArray[0] : null;
  }, [companyDataArray]);

  // Find country name
  const countryName = useMemo(() => {
    if (!companyData || !countries.length || !companyData.country_code) return null;
    const country = countries.find(c => c.country_code === companyData.country_code);
    return country ? country.name : companyData.country_code; // Return code if name not found
  }, [companyData, countries]);

  // --- Loading and Error States ---
  if (isLoading) return <Card><Spin tip="Loading details..." /></Card>; // Use combined loading state
  if (isError) {
      // highlight-start
      let combinedErrorMessage = 'Failed to load data: ';
      if (errorCompany) combinedErrorMessage += `Company (${errorCompany.message}) `;
      if (errorCountries) combinedErrorMessage += `Countries (${errorCountries.message}) `;
      console.error("Error fetching details:", error);
      message.error(combinedErrorMessage.trim());
      // highlight-end
      return (
        <Card>
          <Alert message="Error" description="Could not load details." type="error" showIcon />
          <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')} style={{ marginTop: 16 }}>Back to List</Button>
        </Card>
      );
  }
  if (!companyData) {
      return (
        <Card>
          <Alert message="Company not found" type="warning" showIcon />
          <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')} style={{ marginTop: 16 }}>Back to List</Button>
        </Card>
      );
  }

  // --- Component Rendering (JSX) ---
  return (
    <ProtectedRoute>
    <PageGuard>
    <Card>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col><Title level={4} style={{ margin: 0 }}><CompanyIcon style={{ marginRight: 8 }} /> Company Detail: {companyData.name}</Title></Col>
        <Col><Space>
            <Button action="edit" icon={<EditOutlined />} onClick={() => navigate(`/to/companies/edit/${companyId}`)}>Edit</Button>
            <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')}>Back to List</Button>
        </Space></Col>
      </Row>

      {/* Descriptions */}
      <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} size="small">
        <Descriptions.Item label={<strong>ID</strong>} span={1}>{companyData.id}</Descriptions.Item>
        <Descriptions.Item label={<strong>Name</strong>} span={1}>{companyData.name || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Short Code</strong>} span={1}>{companyData.short_code || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Relation</strong>} span={1}>
          {getRelationLabel(companyData.relation)}
        </Descriptions.Item>
        {/* highlight-start */}
        <Descriptions.Item label={<strong>Country</strong>} span={1}>
          {countryName || '-'}
        </Descriptions.Item>
        {/* highlight-end */}
        <Descriptions.Item label={<strong>Phone</strong>} span={1}>{companyData.phone || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Email</strong>} span={1}>{companyData.email || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Category</strong>} span={1}>{formatEnumString(companyData.category)}</Descriptions.Item>
        <Descriptions.Item label={<strong>Area</strong>} span={1}>{formatEnumString(companyData.area)}</Descriptions.Item>
        <Descriptions.Item label={<strong>Status</strong>} span={1}><Tag color={getStatusColor(companyData.status)}>{formatEnumString(companyData.status)}</Tag></Descriptions.Item>
        <Descriptions.Item label={<strong>Address</strong>} span={2}>{companyData.address || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Created At</strong>} span={1}>{companyData.created_at ? dayjs(companyData.created_at).format('DD MMMM YYYY, HH:mm:ss') : '-'}</Descriptions.Item>
      </Descriptions>
    </Card>
    </PageGuard>
    </ProtectedRoute>
  );
};

export default ToCompDetail;
